const API_BASE = import.meta.env.VITE_API_BASE || "";

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...options,
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(data.error?.message || `HTTP ${res.status}`);
  }

  return data as T;
}

export const api = {
  signup: (email: string, password: string) =>
    request<{ success: boolean; user: { id: string; email: string } }>("/api/auth/signup", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  login: (email: string, password: string) =>
    request<{
      success: boolean;
      user: { id: string; email: string; providerConnected: boolean; providerType: string | null };
    }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  logout: () => request<{ success: boolean }>("/api/auth/logout", { method: "POST" }),

  session: () =>
    request<{
      success: boolean;
      user: { id: string; email: string; providerConnected: boolean; providerType: string | null };
    }>("/api/auth/session"),

  health: () => request<{ ok: boolean; service: string }>("/api/auth/health"),

  deleteAccount: () => request<{ success: boolean }>("/api/account/delete", { method: "POST" }),

  onboardingPending: () => request<{ pending: boolean }>("/api/auth/onboarding/pending"),

  onboardingComplete: (provider?: string) =>
    request<{ success: boolean }>("/api/auth/onboarding/complete", {
      method: "POST",
      body: JSON.stringify({ provider }),
    }),

  testAccountLogin: (userId?: string, email?: string) =>
    request<{
      success: boolean;
      user: { id: string; email: string; providerConnected: boolean; providerType: string | null };
    }>("/api/auth/test/account/login", {
      method: "POST",
      body: JSON.stringify({ userId, email }),
    }),

  oauthExchange: (provider: string, code: string, codeVerifier: string, redirectUri: string) =>
    request<{ success: boolean; tokens: { accessToken: string; refreshToken: string | null; expiresIn: number | null } }>(
      "/api/oauth/exchange",
      {
        method: "POST",
        body: JSON.stringify({ provider, code, codeVerifier, redirectUri }),
      },
    ),

  oauthRefresh: (provider: string, refreshToken: string) =>
    request<{ success: boolean; tokens: { accessToken: string; expiresIn: number | null } }>(
      "/api/oauth/refresh",
      {
        method: "POST",
        body: JSON.stringify({ provider, refreshToken }),
      },
    ),
};
